import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  LocationOn as LocationOnIcon,
  Badge as BadgeIcon,
  Star as StarIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import useAdminContext from "../../store/contexts/AdminContext";
import useApp from "../../store/contexts/AppContext";
import SOCKET_EVENTS from "../../config/SocketEvents";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";
import FormatDateDurationForMessagesDate from "../../utils/FormatDateDurationForMessagesDate";

const UserDetailPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const { getUserByIdApiCall } = useAdminContext();
  const { appState } = useApp();
  const socket = appState?.socket;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const userData = await getUserByIdApiCall(id);
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        console.error("Failed to fetch user:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Listen for real-time profile updates
  useEffect(() => {
    if (!socket || !id) return;

    const handleProfileUpdate = (data) => {
      // Only update if this is the user we're viewing
      if (data.userId === id) {
        setUser(data.userData);
      }
    };

    socket.on(SOCKET_EVENTS.USER_PROFILE_UPDATED, handleProfileUpdate);

    return () => {
      socket.off(SOCKET_EVENTS.USER_PROFILE_UPDATED, handleProfileUpdate);
    };
  }, [socket, id]);

  const handleBack = () => {
    history.push(ADMIN_ROUTES.users);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
            Back to Users
          </Button>
          <Typography variant="h6">User not found</Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
            User Profile
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* User Profile Card */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Avatar
                src={user.profile_image_url}
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  fontSize: "3rem",
                }}
              >
                {user.username?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                {user.username}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {user.email}
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <Chip
                  label={user.account_type?.toUpperCase() || "BASIC"}
                  variant="outlined"
                />
                {user.is_verified ? (
                  <Chip label="Verified" color="success" variant="outlined" />
                ) : null}
                {user.is_banned ? (
                  <Chip label="Banned" color="error" variant="outlined" />
                ) : (
                  <Chip label="Active" color="success" variant="outlined" />
                )}
                <Chip
                  label={user.is_online ? "Online" : "Offline"}
                  color={user.is_online ? "success" : "default"}
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Stats */}
              <Grid container spacing={2} sx={{ textAlign: "center" }}>
                <Grid item xs={6}>
                  <Typography variant="h6" color="primary">
                    {(user.available_coins || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Coins
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="primary">
                    {(user.available_xp || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    XP
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="primary">
                    {(user.roomsCreated || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rooms Created
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="primary">
                    {(user.roomsJoined || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rooms Joined
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Edit User
                </Button>
                <Button
                  variant="outlined"
                  color={user.is_banned ? "success" : "error"}
                  startIcon={user.is_banned ? <CheckCircleIcon /> : <BlockIcon />}
                  fullWidth
                >
                  {user.is_banned ? "Unban User" : "Ban User"}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Detailed Information */}
          <Grid item xs={12} md={8}>
            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                <Tab label="Account Info" />
                <Tab label="Activity" />
                <Tab label="Financial" />
                <Tab label="Profile & Bio" />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Account Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        User ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: "break-all" }}>
                        {user.id}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Username
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.username}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.phone_number || "Not provided"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Membership Tier
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.account_type?.toUpperCase() || "BASIC"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Verification Status
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.is_verified ? "Verified" : "Not Verified"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Account Created
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {FormatDateDurationForMessagesDate(user.created_at)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Last Login
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.last_login_at
                          ? FormatDateDurationForMessagesDate(user.last_login_at)
                          : "Never"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Last Seen
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.last_seen
                          ? FormatDateDurationForMessagesDate(user.last_seen)
                          : "Never"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.is_online ? "Online" : "Offline"}
                      </Typography>
                    </Box>
                  </Grid>

                  {user.bio && (
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Bio
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          {user.bio}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {user.location && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {user.location}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {user.date_of_birth && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Date of Birth
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(user.date_of_birth).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Ban Information */}
                {user.is_banned && user.ban_info && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "error.main" }}>
                      Ban Information
                    </Typography>
                    <Grid container spacing={2}>
                      {user.ban_info.reason && (
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Reason:</strong> {user.ban_info.reason}
                          </Typography>
                        </Grid>
                      )}
                      {user.ban_info.created_at && (
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Banned on:</strong>{" "}
                            {FormatDateDurationForMessagesDate(user.ban_info.created_at)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              </Paper>
            )}

            {activeTab === 1 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Activity Summary
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                          {(user.roomsCreated || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Rooms Created
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                          {(user.roomsJoined || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Rooms Joined
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                          {(user.active_room_counts || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active Rooms
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Recent Activity
                </Typography>
                <List>
                  {user.recentActivity && user.recentActivity.length > 0 ? (
                    user.recentActivity.map((activity, index) => (
                      <ListItem key={index} divider>
                        <ListItemText
                          primary={activity.action}
                          secondary={FormatDateDurationForMessagesDate(activity.timestamp)}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                      No recent activity
                    </Typography>
                  )}
                </List>
              </Paper>
            )}

            {activeTab === 2 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Financial Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                          🪙 {(user.available_coins || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Available Coins
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                          ${(user.total_spent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Spent (USD)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                          {(user.totalPayments || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Transactions
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                          {user.subscription_status || "None"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Subscription Status
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Payment History
                </Typography>
                {user.payment_history && user.payment_history.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {user.payment_history.map((payment, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {FormatDateDurationForMessagesDate(payment.date || payment.created_at)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              ${typeof payment.amount === 'number'
                                ? payment.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : parseFloat(payment.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              }
                            </TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>
                              {payment.type || payment.payment_type || "N/A"}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={(payment.status || "unknown").toUpperCase()}
                                size="small"
                                color={
                                  payment.status === "completed" ? "success" :
                                  payment.status === "pending" ? "warning" :
                                  payment.status === "failed" ? "error" : "default"
                                }
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                    No payment history available
                  </Typography>
                )}
              </Paper>
            )}

            {activeTab === 3 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Profile Customization & Bio
                </Typography>

                <Grid container spacing={3}>
                  {/* Profile Picture Section */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                          Profile Picture
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <Avatar
                            src={user.profile_image_url}
                            sx={{
                              width: 100,
                              height: 100,
                              fontSize: "2rem",
                            }}
                          >
                            {user.username?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Profile Image URL
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.85rem" }}>
                              {user.profile_image_url || "No profile image set"}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Bio Section */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                          User Bio
                        </Typography>
                        {user.bio ? (
                          <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                              {user.bio}
                            </Typography>
                          </Paper>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            No bio provided
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Personal Information */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                          Personal Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Display Name
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                              {user.display_name || user.username || "N/A"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Location
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, fontStyle: !user.location ? "italic" : "normal", color: !user.location ? "text.secondary" : "text.primary" }}>
                              {user.location || "Not set"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Date of Birth
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, fontStyle: !user.date_of_birth ? "italic" : "normal", color: !user.date_of_birth ? "text.secondary" : "text.primary" }}>
                              {user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : "Not set"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Gender
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, textTransform: "capitalize", fontStyle: !user.gender ? "italic" : "normal", color: !user.gender ? "text.secondary" : "text.primary" }}>
                              {user.gender ? user.gender.replace('_', ' ') : "Not set"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Language Preference
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, textTransform: "uppercase" }}>
                              {user.language_preference || "EN"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Timezone
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, fontStyle: !user.timezone ? "italic" : "normal", color: !user.timezone ? "text.secondary" : "text.primary" }}>
                              {user.timezone || "Not set"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Social Media Links */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                          Social Media & Links
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Website
                            </Typography>
                            {user.website ? (
                              <Typography variant="body2" sx={{ mt: 0.5, wordBreak: "break-all" }}>
                                <a href={user.website} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "underline" }}>
                                  {user.website}
                                </a>
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic", color: "text.secondary" }}>
                                Not set
                              </Typography>
                            )}
                          </Grid>

                          {["twitter", "instagram", "linkedin", "facebook"].map((platform) => {
                            const link = user.social_media_links?.[platform];
                            return (
                              <Grid item xs={12} sm={6} key={platform}>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                                  {platform}
                                </Typography>
                                {link ? (
                                  <Typography variant="body2" sx={{ mt: 0.5, wordBreak: "break-all" }}>
                                    <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "underline" }}>
                                      {link}
                                    </a>
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic", color: "text.secondary" }}>
                                    Not set
                                  </Typography>
                                )}
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Preferences */}
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                          User Preferences
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Email Notifications
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                              {user.email_notifications !== undefined
                                ? (user.email_notifications ? "Enabled" : "Disabled")
                                : "Not set"
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Push Notifications
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                              {user.push_notifications !== undefined
                                ? (user.push_notifications ? "Enabled" : "Disabled")
                                : "Not set"
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Privacy Setting
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, textTransform: "capitalize" }}>
                              {user.privacy_setting || "Default"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Theme Preference
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, textTransform: "capitalize" }}>
                              {user.theme_preference || "System Default"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </AdminLayout>
  );
};

export default UserDetailPage;
